import React, { useEffect, useState, useRef } from 'react';
import { Container, Header, Segment, Label, Button, Icon, Input, Message, Image, Breadcrumb, Card, Form, FormCheckbox } from 'semantic-ui-react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { getTranscript, formatUrlPart, getVotesForTranscript, castVote, registerUser, getUserStatusForTranscript, getTranscriptsForModel, getTranscriptId, SITE_BASE_URL } from '../services/api';
import TopSection from './TopSection';
import DisclaimerLink from './DisclaimerLink';
import { DISCLAIMER_TEXT } from '../constants/disclaimer';
import { toTitleCase } from '../utils/format';

const TranscriptPage = () => {
  const { brand, model, transcriptid } = useParams();
  const [transcript, setTranscript] = useState(null);
  const [votes, setVotes] = useState({ upvotes: 0, downvotes: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [email, setEmail] = useState(localStorage.getItem('userEmail') || '');
  const [isRegistered, setIsRegistered] = useState(false);
  const [hasVoted, setHasVoted] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [otherTranscripts, setOtherTranscripts] = useState([]);
  const [optInChecked, setOptInChecked] = useState(true);
  const [termsChecked, setTermsChecked] = useState(false);
  const [showOptInMessage, setShowOptInMessage] = useState(false);
  const [showTermsMessage, setShowTermsMessage] = useState(false);
  const navigate = useNavigate();
  const articlesRef = useRef(null);
  const transcriptRef = useRef(null);
  const sidebarRef = useRef(null);

  const initialDisplayBrand = toTitleCase(brand);
  const initialDisplayModel = toTitleCase(model);
  const [displayBrand, setDisplayBrand] = useState(initialDisplayBrand);
  const [displayModel, setDisplayModel] = useState(initialDisplayModel);

  useEffect(() => {
    Promise.all([
      getTranscript(brand, model, transcriptid),
      getTranscriptsForModel(brand, model)
    ])
    .then(([transcriptData, transcriptsData]) => {
      if (transcriptData && transcriptData.details && transcriptData.details.vehicle) {
        setTranscript(transcriptData);
        setDisplayBrand(transcriptData.details.vehicle.make || initialDisplayBrand);
        setDisplayModel(transcriptData.details.vehicle.model || initialDisplayModel);
        setOtherTranscripts(transcriptsData.filter(t => getTranscriptId(t) !== transcriptid));
        return getVotesForTranscript(transcriptid);
      } else {
        setTranscript(null);
        setOtherTranscripts([]);
        return null;
      }
    })
    .then(voteData => {
      if (voteData) {
        setVotes({ upvotes: voteData.upvotes, downvotes: voteData.downvotes });
      }
      setIsLoading(false);
    })
    .catch(err => {
      console.error('Error fetching data:', err);
      setIsLoading(false);
    });
  }, [brand, model, transcriptid, initialDisplayBrand, initialDisplayModel]);

  useEffect(() => {
    if (email && transcriptid) {
      const registrationKey = `isRegistered_${transcriptid}_${email}`;
      const storedRegistration = localStorage.getItem(registrationKey);
      if (storedRegistration === 'true') {
        setIsRegistered(true);
      } else {
        getUserStatusForTranscript(transcriptid, email)
          .then(status => {
            setIsRegistered(status.registered);
            setHasVoted(status.voted);
            if (status.registered) {
              localStorage.setItem(registrationKey, 'true');
            }
          })
          .catch(err => console.error('Error fetching user status:', err));
      }
    }
  }, [email, transcriptid]);

  useEffect(() => {
    const updateSidebarHeight = () => {
      if (transcriptRef.current && sidebarRef.current) {
        const transcriptHeight = transcriptRef.current.offsetHeight;
        sidebarRef.current.style.maxHeight = `${transcriptHeight}px`;
      }
    };

    updateSidebarHeight();
    window.addEventListener('resize', updateSidebarHeight);
    return () => window.removeEventListener('resize', updateSidebarHeight);
  }, [transcript]);

  const handleSearch = () => {
    if (searchQuery.trim() !== '') {
      navigate(`/search/${formatUrlPart(searchQuery)}`);
    }
  };

  const handleRegister = () => {
    if (!termsChecked) {
      setShowTermsMessage(true);
      setShowOptInMessage(false);
      return;
    }
    if (!optInChecked) {
      setShowOptInMessage(true);
      setShowTermsMessage(false);
      return;
    }
    if (!email) {
      alert('Please enter an email address.');
      return;
    }
    registerUser(email, transcriptid)
      .then(() => {
        const registrationKey = `isRegistered_${transcriptid}_${email}`;
        localStorage.setItem('userEmail', email);
        localStorage.setItem(registrationKey, 'true');
        setIsRegistered(true);
        setShowOptInMessage(false);
        setShowTermsMessage(false);

        const vehicle = transcript?.details?.vehicle;
        const make = vehicle?.make ?? displayBrand ?? '';
        const vehicleModel = vehicle?.model ?? displayModel ?? '';
        const year =
          vehicle?.year != null && vehicle.year !== ''
            ? String(vehicle.year)
            : '';

        // Identify user in Rudderstack and push event for GTM
        if (window.rudderanalytics) {
          window.rudderanalytics.identify({
            email,
            email_subscribe: optInChecked === true ? 'Opted In' : 'Subscribed',
            make,
            model: vehicleModel,
            year,
          });
        }
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'support_library_unlock',
          make,
          model: vehicleModel,
          year,
        });
      })
      .catch(err => {
        console.error('Error registering user:', err);
        alert('Registration failed. Please try again.');
      });
  };

  const handleVote = (voteType) => {
    if (!email) {
      alert('Please enter an email address to vote.');
      return;
    }
    if (!isRegistered) {
      alert('Please register to vote.');
      return;
    }
    if (hasVoted) {
      alert('You have already voted on this transcript.');
      return;
    }
    castVote(transcriptid, voteType, email)
      .then(() => {
        setVotes(prev => ({
          ...prev,
          [voteType + 'votes']: prev[voteType + 'votes'] + 1
        }));
        setHasVoted(true);
      })
      .catch(err => {
        console.error('Error casting vote:', err);
        alert('Voting failed. Please try again.');
      });
  };

  const normalizeTranscript = (text) => {
    return text.replace(/\*\*(User|Technician)::\*\*/g, '**$1:**');
  };

  const renderTranscript = (text, maxLines = null) => {
    const normalizedText = normalizeTranscript(text);
    // Split on newline(s) + optional whitespace so we get one block per speaker (API uses "  \n  \n" between blocks)
    const paragraphs = normalizedText.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
    const limitedParagraphs = maxLines ? paragraphs.slice(0, maxLines) : paragraphs;
    return limitedParagraphs.map((para, index) => {
      // [\s\S]* captures content including newlines within a speaker's block
      const match = para.match(/^\*\*(User|Technician)\s*:\*\*\s*([\s\S]*)/);
      if (match) {
        const speaker = match[1];
        const content = match[2].trim();
        return (
          <div key={index} className={`transcript-line ${speaker.toLowerCase()}-line`}>
            <strong>{speaker}:</strong> {content}
          </div>
        );
      } else {
        return <p key={index}>{para}</p>;
      }
    });
  };

  const truncateText = (text, limit) => {
    if (text == null || typeof text !== 'string') return '';
    if (text.length <= limit) return text;
    const truncated = text.substring(0, limit);
    const lastSpace = truncated.lastIndexOf(' ');
    return lastSpace > 0 ? truncated.substring(0, lastSpace) + '...' : truncated + '...';
  };

  if (isLoading) {
    return (
      <>
        <TopSection
          title={`${displayBrand} ${displayModel} Customer Support`}
          showLogo={false}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          handleSearch={handleSearch}
        />
        <Container className="main-container">
          <p style={{ marginBottom: '20px' }}>
            <DisclaimerLink />
          </p>
          <div>Loading...</div>
        </Container>
      </>
    );
  }

  if (!transcript) {
    return (
      <>
        <TopSection
          title={`${displayBrand} ${displayModel} Customer Support`}
          showLogo={false}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          handleSearch={handleSearch}
        />
        <Container className="main-container">
          <p style={{ marginBottom: '20px' }}>
            <DisclaimerLink />
          </p>
          <div>Transcript File not found</div>
        </Container>
      </>
    );
  }

  const capitalizedProblem = transcript.details.problems && transcript.details.problems[0] ? toTitleCase(transcript.details.problems[0]) : 'Transcript Details';

  const baseUrl = SITE_BASE_URL.replace(/\/$/, '');
  const canonicalUrl = `${baseUrl}/brand/${formatUrlPart(brand)}/model/${formatUrlPart(model)}/${transcriptid}`;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": baseUrl
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": displayBrand,
        "item": `${baseUrl}/brand/${formatUrlPart(brand)}`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": displayModel,
        "item": `${baseUrl}/brand/${formatUrlPart(brand)}/model/${formatUrlPart(model)}`
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": capitalizedProblem,
        "item": canonicalUrl
      }
    ]
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${canonicalUrl}#article`,
    "headline": `Fix ${capitalizedProblem} on ${displayBrand} ${displayModel}`,
    "description": `Learn how to fix ${capitalizedProblem} on your ${displayBrand} ${displayModel} with this detailed transcript from certified RV technicians.`,
    "author": {
      "@type": "Organization",
      "name": "Blue Compass RV Support Team"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Blue Compass RV",
      "logo": {
        "@type": "ImageObject",
        "url": `${baseUrl}/rv-complete-logo.png`
      }
    },
    "datePublished": transcript.createdAt ? new Date(transcript.createdAt).toISOString() : new Date().toISOString(),
    "dateModified": transcript.updatedAt ? new Date(transcript.updatedAt).toISOString() : new Date().toISOString(),
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl
    },
    "image": `${baseUrl}/rv-complete-logo.png`,
    "keywords": `${displayBrand}, ${displayModel}, ${capitalizedProblem}, RV troubleshooting, fix RV issues`
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      breadcrumbSchema,
      articleSchema
    ]
  };

  const title = `Fix ${capitalizedProblem} - ${displayBrand} ${displayModel} - RESOLVED`;
  const description = `Learn how to fix ${capitalizedProblem} on ${displayBrand} ${displayModel}. Step-by-step transcript from a certified RV technician support call.`;

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={`${baseUrl}/bcrvlogo.png`} />
        <meta property="og:site_name" content="Blue Compass RV Technical Support" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <TopSection
        title={`How to fix ${capitalizedProblem} on ${displayBrand} ${displayModel}`}
        showLogo={false}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        handleSearch={handleSearch}
      />
      <main>
      <Container className="main-container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
          <Breadcrumb className="breadcrumb" style={{ margin: 0 }}>
            <Breadcrumb.Section link onClick={() => navigate('/')}>
              <span style={{color: 'blue'}}>Home</span>
            </Breadcrumb.Section>
            <Breadcrumb.Divider icon="right angle" />
            <Breadcrumb.Section link onClick={() => navigate(`/brand/${formatUrlPart(brand)}`)}>
              {displayBrand}
            </Breadcrumb.Section>
            <Breadcrumb.Divider icon="right angle" />
            <Breadcrumb.Section link onClick={() => navigate(`/brand/${formatUrlPart(brand)}/model/${formatUrlPart(model)}`)}>
              {displayModel}
            </Breadcrumb.Section>
            <Breadcrumb.Divider icon="right angle" />
            <Breadcrumb.Section active>
              {capitalizedProblem}
            </Breadcrumb.Section>
          </Breadcrumb>
          <DisclaimerLink />
        </div>

        <div className="content-wrapper">
          <div className="main-content">
            {isRegistered && (
              <div className="vote-section">
                <span className="vote-question">Was this article helpful?</span>
                <div className="vote-buttons">
                  <Button
                    icon
                    labelPosition="left"
                    onClick={() => handleVote('up')}
                    disabled={hasVoted}
                  >
                    <Icon name="thumbs up" />
                    {votes.upvotes}
                  </Button>
                  <Button
                    icon
                    labelPosition="left"
                    onClick={() => handleVote('down')}
                    disabled={hasVoted}
                  >
                    <Icon name="thumbs down" />
                    {votes.downvotes}
                  </Button>
                </div>
              </div>
            )}

            <Segment ref={transcriptRef} className="transcript-segment">
              <p>
                <strong>Vehicle:</strong> {transcript.details.vehicle.make}{' '}
                {transcript.details.vehicle.model} ({transcript.details.vehicle.year})
              </p>
              {transcript.details.vehicle.description && (
                <p>
                  <strong>Description:</strong>{' '}
                  {showFullDescription
                    ? transcript.details.vehicle.description
                    : truncateText(transcript.details.vehicle.description, 150)}
                  {transcript.details.vehicle.description.length > 150 && (
                    <span
                      className="view-toggle"
                      onClick={() => setShowFullDescription(!showFullDescription)}
                    >
                      {showFullDescription ? 'view less' : 'view more'}
                    </span>
                  )}
                </p>
              )}
              <p><strong>Problems Solved in Article:</strong></p>
              <Label.Group>
                {transcript.details.problems.map((problem, idx) => (
                  <Label key={idx} basic color="blue">
                    {toTitleCase(problem)}
                  </Label>
                ))}
              </Label.Group>
              <Header as="h2">{`Step by Step Troubleshooting ${capitalizedProblem} With A Technician`}</Header>
              <div className="transcript">
                {isRegistered ? (
                  renderTranscript(transcript.formattedTranscript)
                ) : (
                  <>
                    {renderTranscript(transcript.formattedTranscript, 3)}
                    <div className="registration-prompt" style={{ backgroundColor: '#f9f9f9', border: '2px solid #01274c', padding: '20px', borderRadius: '10px', marginTop: '20px' }}>
                      <Message
                        attached
                        header={<Header as="h3" style={{ color: '#01274c', fontSize: '24px' }}>Register Below to Unlock the Full Solution</Header>}
                        icon={<Icon name="key" size="large" style={{ color: '#01274c' }} />}
                        style={{  border: 'none', boxShadow: 'none' }}
                      />
                      <Form className="attached fluid segment" style={{ backgroundColor: 'transparent', border: 'none', padding: '0' }}>
                        <Input
                          fluid
                          placeholder="Enter Your Email"
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            localStorage.setItem('userEmail', e.target.value);
                          }}
                          className="email-input"
                          style={{marginBottom: '15px', padding: '12px', fontSize: '18px'}}
                        />
                        <Form.Field error={showTermsMessage}>
                          <FormCheckbox
                            label="I have read and agree to the terms below."
                            checked={termsChecked}
                            onChange={(e, { checked }) => { setTermsChecked(checked); setShowTermsMessage(false); }}
                            required
                          />
                        </Form.Field>
                        <Form.Field error={showOptInMessage}>
                          <FormCheckbox
                            label="I would like to receive offers and promotional marketing emails from Blue Compass."
                            checked={optInChecked}
                            onChange={(e, { checked }) => { setOptInChecked(checked); setShowOptInMessage(false); }}
                            required
                          />
                          <p style={{ marginTop: '4px', marginLeft: '1.8em', fontSize: '0.95em' }}>
                            See our{' '}
                            <a href="https://www.bluecompassrv.com/mobile-marketing-terms-of-service" target="_blank" rel="noopener noreferrer">Terms</a>
                            {' & '}
                            <a href="https://www.bluecompassrv.com/mobile-marketing-privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                            {' for more information. (Easily unsubscribe at any time.)'}
                          </p>
                        </Form.Field>
                        <Button
                          size="large"
                          onClick={handleRegister}
                          style={{ backgroundColor: '#01274c', color: 'white', fontSize: '18px', fontWeight: 'bold', padding: '15px 30px', borderRadius: '5px' }}
                        >
                          Register & Reveal
                        </Button>
                      </Form>
                      {showTermsMessage && (
                        <Message attached="bottom" error style={{ marginTop: '10px' }}>
                          <Icon name="warning" />
                          You must read and agree to the terms below to continue.
                        </Message>
                      )}
                      {showOptInMessage && (
                        <Message attached="bottom" error style={{ marginTop: '10px' }}>
                          <Icon name="warning" />
                          You must agree to receive updates to view this content.
                        </Message>
                      )}
                      <Segment style={{ marginTop: '20px', padding: '16px', backgroundColor: '#f5f5f5', maxHeight: '200px', overflowY: 'auto' }}>
                        <p style={{ fontSize: '12px', lineHeight: '1.5', margin: 0, whiteSpace: 'pre-wrap' }}>{DISCLAIMER_TEXT}</p>
                      </Segment>
                      <div className="blurred-transcript" style={{ filter: 'blur(3px)', opacity: '0.5', marginTop: '20px' }}>
                        {renderTranscript(transcript.formattedTranscript)}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {isRegistered && (
                <div className="vote-feedback">
                  <Header as="h3" textAlign="center" className="feedback-header">
                    Was this article helpful?
                  </Header>
                  <div className="vote-buttons">
                    <Button
                      color="green"
                      size="large"
                      onClick={() => handleVote('up')}
                      disabled={hasVoted}
                    >
                      <Icon name="thumbs up" /> Yes ({votes.upvotes})
                    </Button>
                    <Button
                      color="red"
                      size="large"
                      onClick={() => handleVote('down')}
                      disabled={hasVoted}
                    >
                      <Icon name="thumbs down" /> No ({votes.downvotes})
                    </Button>
                  </div>
                </div>
              )}
            </Segment>
            <div className='below-content'>
              <Segment className="blue-segment main-content-segment">
                <Header as="h2" className="cta-header">
                  RV Technician Support When You Need It
                </Header>
                <Image
                  src={`${process.env.PUBLIC_URL || ''}/rv-complete-logo.png`}
                  alt="RV Complete Logo - Expert Technical Support"
                  size="large"
                  centered
                  className="cta-image"
                />
                <a href="https://rvcomplete.com" target="_blank" rel="noopener noreferrer">
                  <Button primary size="large">
                    Sign Up
                  </Button>
                </a>
              </Segment>

              <Segment className="blue-segment main-content-segment">
                <Image
                  src={`${process.env.PUBLIC_URL || ''}/bcrvlogo.png`}
                  alt="Blue Compass RV Sales"
                  size="large"
                  centered
                  className="cta-image"
                />
                <div className="cta-buttons">
                  <a href="https://www.bluecompassrv.com/rv-service" target="_blank" rel="noopener noreferrer">
                    <Button primary size="large" fluid>
                      Schedule Service
                    </Button>
                  </a>
                  <a href="https://www.bluecompassrv.com/value-your-trade" target="_blank" rel="noopener noreferrer">
                    <Button primary size="large" fluid>
                      Get Trade In Value
                    </Button>
                  </a>
                  <a href="https://www.bluecompassrv.com/new-rvs-for-sale" target="_blank" rel="noopener noreferrer">
                    <Button primary size="large" fluid>
                      Shop New RVs
                    </Button>
                  </a>
                  <a href="https://www.bluecompassrv.com/used-rvs-for-sale" target="_blank" rel="noopener noreferrer">
                    <Button primary size="large" fluid>
                      Shop Used RVs
                    </Button>
                  </a>
                  <a href="https://www.bluecompassrv.com/locations" target="_blank" rel="noopener noreferrer">
                    <Button primary size="large" fluid>
                      Find a Dealer
                    </Button>
                  </a>
                </div>
              </Segment>
            </div>

            {otherTranscripts.length > 0 && (
              <Segment ref={articlesRef} className="main-content-segment articles-segment">
                <Header as="h2" textAlign="center">
                  Other Troubleshooting Articles for {displayBrand} {displayModel}
                </Header>
                <Card.Group itemsPerRow={1} stackable className="card-group">
                  {otherTranscripts.map(call => {
                    const id = getTranscriptId(call);
                    if (!id) return null;
                    return (
                    <Card
                      key={id}
                      href={`${process.env.PUBLIC_URL || ''}/brand/${formatUrlPart(brand)}/model/${formatUrlPart(model)}/${id}`}
                      raised
                      className="custom-card"
                    >
                      <Card.Content>
                        <Card.Header className="card-header">
                          {call.details && call.details.problems && call.details.problems.length > 0
                            ? call.details.problems[0]
                            : 'Call Transcript'}
                        </Card.Header>
                        <Card.Meta className="card-meta">
                          {call.details && call.details.resolvedOnCall ? 'Resolved on Call' : 'Unresolved'}
                        </Card.Meta>
                        <Card.Description>
                          <strong>Problems:</strong>
                          <Label.Group>
                            {call.details && call.details.problems && call.details.problems.length > 0
                              ? call.details.problems.map((problem, idx) => (
                                  <Label basic color="blue" key={idx}>
                                    {problem}
                                  </Label>
                                ))
                              : <span>No problems listed</span>}
                          </Label.Group>
                        </Card.Description>
                      </Card.Content>
                    </Card>
                    );
                  })}
                </Card.Group>
              </Segment>
            )}
            <div className="bottom-spacer"></div>
          </div>

          <div className="sidebar" ref={sidebarRef}>
            <Segment className="blue-segment sidebar-segment">
              <Header as="h2" className="cta-header">
                RV Tech Support When You Need It
              </Header>
              <Image
                src={`${process.env.PUBLIC_URL || ''}/rv-complete-logo.png`}
                alt="RV Complete Logo - Expert Technical Support"
                size="large"
                centered
                className="cta-image"
              />
              <a href="https://rvcomplete.com" target="_blank" rel="noopener noreferrer">
                <Button primary size="large" fluid>
                  Sign Up
                </Button>
              </a>
            </Segment>
            <Segment className="blue-segment sidebar-segment">
              <Image
                src={`${process.env.PUBLIC_URL || ''}/bcrvlogo.png`}
                alt="Blue Compass RV Sales"
                size="large"
                centered
                className="cta-image"
              />
              <div className="cta-buttons">
                <a href="https://www.bluecompassrv.com/rv-service" target="_blank" rel="noopener noreferrer">
                  <Button primary size="large" fluid>
                    Schedule Service
                  </Button>
                </a>
                <a href="https://www.bluecompassrv.com/value-your-trade" target="_blank" rel="noopener noreferrer">
                  <Button primary size="large" fluid>
                    Get Trade In Value
                  </Button>
                </a>
                <a href="https://www.bluecompassrv.com/new-rvs-for-sale" target="_blank" rel="noopener noreferrer">
                  <Button primary size="large" fluid>
                    Shop New RVs
                  </Button>
                </a>
                <a href="https://www.bluecompassrv.com/used-rvs-for-sale" target="_blank" rel="noopener noreferrer">
                  <Button primary size="large" fluid>
                    Shop Used RVs
                  </Button>
                </a>
                <a href="https://www.bluecompassrv.com/locations" target="_blank" rel="noopener noreferrer">
                  <Button primary size="large" fluid>
                    Find a Dealer
                  </Button>
                </a>
              </div>
            </Segment>
          </div>
        </div>

        <div className="mobile-buttons">
          <a href="https://www.bluecompassrv.com/rv-service" target="_blank" rel="noopener noreferrer">
            <Button primary size="large">
              Schedule Service
            </Button>
          </a>
          <a href="https://www.bluecompassrv.com/value-your-trade" target="_blank" rel="noopener noreferrer">
            <Button primary size="large">
              Get Trade In Value
            </Button>
          </a>
        </div>
      </Container>
      </main>
    </>
  );
};

export default TranscriptPage;